
import React, { useState, useEffect, useRef } from 'react';
import { User, ChatSession, ChatMessage } from '@/types';
import { 
  subscribeToChatSessions, 
  subscribeToSessionMessages,
  deleteChatSession,
  checkOldChatSessionsExist, // Import new function
  migrateChatSessions // Import new function
} from '@/services/database';
import { 
  MessageSquare, 
  Trash2, 
  User as UserIcon, 
  Sparkles, 
  Loader2,
  Users,
  MessageCircleQuestion,
  History,
  Info,
  ArrowRightCircle,
  CheckCircle2,
  AlertTriangle,
  Database
} from 'lucide-react';
import { UserRole } from '@/types'; // Import UserRole

interface ChatHistoryReportProps {
  users: User[];
  currentUser: User; // Add currentUser prop
}

// Helper para formatar mensagens (copiado e adaptado do ChatInterface para consistência)
const formatMessage = (text: string) => {
  if (!text) return '';
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-black text-slate-900">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export const ChatHistoryReport: React.FC<ChatHistoryReportProps> = ({ users, currentUser }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userSessions, setUserSessions] = useState<ChatSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [currentSessionMessages, setCurrentSessionMessages] = useState<ChatMessage[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Migration States
  const [hasOldSessions, setHasOldSessions] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(0); // 0-100
  const [migrationStatusMessage, setMigrationStatusMessage] = useState<string | null>(null);
  const [migrationError, setMigrationError] = useState<string | null>(null);


  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAdmin = currentUser.role === UserRole.ADMIN;

  // Check for old chat sessions on component mount (for admins)
  useEffect(() => {
    const checkOldSessions = async () => {
      if (isAdmin) {
        const exists = await checkOldChatSessionsExist();
        setHasOldSessions(exists);
        if (exists) {
          setMigrationStatusMessage("Sessões de chat antigas detectadas. Requer migração.");
        }
      }
    };
    checkOldSessions();
  }, [isAdmin]);

  // Effect to load sessions for the selected user
  useEffect(() => {
    if (!selectedUserId) {
      setUserSessions([]);
      setSelectedSessionId(null);
      return;
    }

    setIsLoadingSessions(true);
    setError(null);
    const unsubscribe = subscribeToChatSessions(selectedUserId, (sessions) => {
      setUserSessions(sessions);
      if (sessions.length > 0 && !selectedSessionId) {
        setSelectedSessionId(sessions[0].id); // Select the first session by default
      } else if (selectedSessionId && !sessions.some(s => s.id === selectedSessionId)) {
        // If the selected session was deleted or no longer exists
        setSelectedSessionId(sessions.length > 0 ? sessions[0].id : null);
      }
      setIsLoadingSessions(false);
    });

    return () => unsubscribe();
  }, [selectedUserId, selectedSessionId]); // selectedSessionId also a dependency to reset if session is deleted

  // Effect to load messages for the selected session
  useEffect(() => {
    if (!selectedSessionId) {
      setCurrentSessionMessages([]);
      return;
    }

    setIsLoadingMessages(true);
    setError(null);
    const unsubscribe = subscribeToSessionMessages(selectedSessionId, (session) => {
      if (session) {
        setCurrentSessionMessages(session.messages);
      } else {
        // Session not found or deleted
        setCurrentSessionMessages([]);
        setSelectedSessionId(null); // Reset selected session
        setError("Sessão não encontrada ou foi deletada.");
      }
      setIsLoadingMessages(false);
    });

    return () => unsubscribe();
  }, [selectedSessionId]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentSessionMessages, isLoadingMessages]);

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation(); // Prevents click from bubbling up and selecting the session
    if (window.confirm('Tem certeza que deseja excluir esta conversa? Esta ação é irreversível.')) {
      try {
        await deleteChatSession(sessionId);
        if (selectedSessionId === sessionId) {
          setSelectedSessionId(null); // Clears selected session if it's the one being viewed
        }
        alert('Conversa excluída com sucesso!');
      } catch (err: any) {
        console.error('Erro ao excluir conversa:', err);
        setError(`Erro ao excluir conversa: ${err.message}`);
      }
    }
  };

  const handleMigrateSessions = async () => {
    if (!isAdmin || isMigrating) return;

    if (!window.confirm("Você tem certeza que deseja iniciar a migração das sessões de chat antigas? Esta ação pode levar alguns minutos e é irreversível.")) {
      return;
    }

    setIsMigrating(true);
    setMigrationError(null);
    setMigrationStatusMessage("Iniciando migração...");
    setMigrationProgress(0);

    try {
      await migrateChatSessions((progress, message) => {
        setMigrationProgress(progress);
        setMigrationStatusMessage(message);
      });
      setHasOldSessions(false); // After successful migration, no old sessions should exist
      setMigrationStatusMessage("Migração concluída com sucesso!");
      alert("Migração de sessões de chat finalizada com sucesso!");
    } catch (err: any) {
      setMigrationError(`Falha na migração: ${err.message}`);
      setMigrationStatusMessage("Erro durante a migração.");
      alert(`Falha na migração: ${err.message}`);
    } finally {
      setIsMigrating(false);
    }
  };


  const getDisplayName = (user: User | null) => user ? `${user.fullName} (${user.email})` : 'Selecione um Usuário';
  // const getSelectedUser = () => users.find(u => u.id === selectedUserId); // Not currently used, but keep for consistency if needed later

  return (
    <div className="flex h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
      {/* Sidebar de Seleção de Usuário e Sessões */}
      <div className="flex flex-col w-80 bg-slate-900 border-r border-slate-800">
        <div className="p-4 border-b border-slate-800">
          <label htmlFor="user-select" className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
            Usuário
          </label>
          <select
            id="user-select"
            value={selectedUserId || ''}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full px-4 py-3 bg-slate-800 text-white rounded-xl border border-slate-700 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
          >
            <option value="">Selecione um Usuário</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {getDisplayName(user)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isAdmin && hasOldSessions && (
            <div className={`p-4 mx-2 my-2 rounded-xl border ${migrationError ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'} text-center animate-fade-in`}>
              {migrationError ? (
                <div className="flex flex-col items-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 mb-2" />
                  <p className="text-red-800 font-medium text-sm">Erro na migração: {migrationError}</p>
                  <button onClick={handleMigrateSessions} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50" disabled={isMigrating}>
                    Tentar Novamente
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Database className="w-6 h-6 text-orange-600 mb-2" />
                  <p className="text-orange-800 font-medium text-sm">Ainda há sessões antigas para migrar.</p>
                  <p className="text-orange-700 text-xs mt-1">Isso pode causar histórico incompleto.</p>
                  <button 
                    onClick={handleMigrateSessions} 
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    disabled={isMigrating}
                  >
                    {isMigrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightCircle size={16} />}
                    {isMigrating ? `Migrando... ${migrationProgress}%` : 'Iniciar Migração'}
                  </button>
                  {isMigrating && migrationStatusMessage && (
                    <p className="text-xs text-slate-600 mt-2">{migrationStatusMessage}</p>
                  )}
                  {!isMigrating && migrationStatusMessage && migrationProgress === 100 && (
                    <p className="text-xs text-green-700 mt-2 flex items-center gap-1"><CheckCircle2 size={12} /> {migrationStatusMessage}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-widest">
            Sessões
          </p>
          {selectedUserId && isLoadingSessions && (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Carregando sessões...
            </div>
          )}
          {userSessions.length === 0 && selectedUserId && !isLoadingSessions && (
            <div className="p-4 text-center text-slate-500">
              <MessageCircleQuestion className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Nenhuma sessão encontrada para este usuário.
            </div>
          )}
          {userSessions.map(session => (
            <div
              key={session.id}
              onClick={() => setSelectedSessionId(session.id)}
              className={`group w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer ${
                selectedSessionId === session.id ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800/50'
              }`}
            >
              <History size={16} className="flex-shrink-0" />
              <span className="text-sm font-medium truncate flex-1">{session.title}</span>
              <button
                onClick={(e) => handleDeleteSession(e, session.id)}
                className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-all"
                title="Excluir conversa"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Área de Exibição de Mensagens */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative">
        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 bg-slate-50/30">
          {!selectedUserId && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Users className="w-20 h-20 mb-4 opacity-20" />
              <p className="text-xl font-bold">Selecione um Usuário</p>
              <p className="text-sm mt-2">Para visualizar o histórico de conversas da IA.</p>
            </div>
          )}

          {selectedUserId && !selectedSessionId && !isLoadingSessions && userSessions.length > 0 && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <History className="w-20 h-20 mb-4 opacity-20" />
              <p className="text-xl font-bold">Selecione uma Conversa</p>
              <p className="text-sm mt-2">Para visualizar as mensagens trocadas com a IA.</p>
            </div>
          )}

          {selectedUserId && !selectedSessionId && !isLoadingSessions && userSessions.length === 0 && (
             <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <MessageCircleQuestion className="w-20 h-20 mb-4 opacity-20" />
              <p className="text-xl font-bold">Nenhuma Conversa para este Usuário</p>
              <p className="text-sm mt-2">Este usuário ainda não interagiu com a IA.</p>
            </div>
          )}

          {selectedUserId && selectedSessionId && isLoadingMessages && (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Carregando mensagens...
            </div>
          )}
          
          {selectedUserId && selectedSessionId && !isLoadingMessages && currentSessionMessages.length === 0 && !error && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Info className="w-20 h-20 mb-4 opacity-20" />
              <p className="text-xl font-bold">Nenhuma mensagem nesta sessão.</p>
              <p className="text-sm mt-2">Pode ter sido uma sessão vazia ou houve um erro.</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl flex items-start gap-2 mb-6 animate-fade-in text-center">
              <Info className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-bold text-lg mb-1">Erro!</p>
                <p className="text-base leading-relaxed">{error}</p>
              </div>
            </div>
          )}

          {currentSessionMessages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
              <div className={`max-w-[85%] rounded-[32px] p-6 shadow-sm border ${msg.role === 'user' ? 'bg-blue-600 text-white border-blue-500 rounded-br-none' : 'bg-white border-slate-200 text-slate-800 rounded-bl-none'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-xl flex-shrink-0 ${msg.role === 'user' ? 'bg-blue-500' : 'bg-blue-50'}`}>
                    {msg.role === 'user' ? <UserIcon size={18} /> : <Sparkles size={18} className="text-blue-600" />}
                  </div>
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap">
                    {formatMessage(msg.text)}
                    <div className={`text-[10px] mt-2 ${msg.role === 'user' ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(msg.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};
